import { Flex, Text, useDisclosure, Tooltip } from "@chakra-ui/react";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { BaseTableFilters } from "@/components/BaseTableFilters/BaseTableFilters";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../../components/Layout/Layout";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import { fetchAllProjects } from "../../store/slices/projects";
import { fetchCustomers } from "../../store/slices/customers";
import { useEffect, useMemo, useState } from "react";
import { formatDate } from "../../helpers/formatDate";
import { Pagination } from "../../components/Pagination/Pagination";
import { useSorting } from "../../hooks/useSorting";
import api from "../../api";
import { Recruiter } from "@/model/recruiters";
import { Project } from "@/model/projects";
import { StatusCell } from "@/components/StatusCell/StatusCell";
import { ProjectsPopupFilter } from "./components/ProjectsPopupFilter";
import { ProjectsFilter, defaultProjectsFilterFields } from "./_helpers";
import { toUnix } from "@/utils/date";
import { getOffset } from "@/utils/pagination";
import { useSessionStorageFilters } from "@/hooks/useSessionStorageFilters";

const columnHelper = createColumnHelper<Project>();

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isOpen: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose } = useDisclosure();
  const handleCreate = () => {
    navigate("create");
  };

  const { projects, isLoading } = useAppSelector((state: RootState) => state.projects);
  const { guides } = useAppSelector((state: RootState) => state.guides);

  const [recruiters, setRecruiters] = useState<Recruiter[]>([]);

  const [totalItems, setTotalItems] = useState(0);

  const [searchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 20;

  const { sortColumn, sortDirection, handleSort } = useSorting("project.created_at");

  // Используем универсальный хук для работы с фильтрами
  const { formik, handleSearch, handleApplyFilters, handleResetFilters } = useSessionStorageFilters<ProjectsFilter>({
    storageKey: "projectsPageFilters",
    defaultValues: defaultProjectsFilterFields,
  });

  useEffect(() => {
    const offset = getOffset(page, limit);
    const { created_at_from, created_at_to, ...restFilters } = formik.values;
    const computedFilters = {
      ...restFilters,
      created_at_from: toUnix(created_at_from),
      created_at_to: toUnix(created_at_to),
      sort_column: sortColumn || undefined,
      sort_direction: sortDirection || undefined,
      limit: String(limit),
      offset: String(offset),
    };
    dispatch(fetchAllProjects({ params: { filters: computedFilters } })).then((res: any) => {
      setTotalItems(res.payload.count);
    });
  }, [dispatch, page, limit, formik.values, sortColumn, sortDirection]);

  useEffect(() => {
    let isMounted = true;
    api.recruiters
      .recruiters({ customer: true })
      .then(({ data }) => {
        if (isMounted) setRecruiters(data || []);
      })
      .catch(() => {
        if (isMounted) setRecruiters([]);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (formik.values.customer_id) count += 1;
    if (formik.values.status) count += 1;
    if (formik.values.legal_entity_id) count += 1;
    if (formik.values.responsible_id) count += 1;
    if (formik.values.created_at_from) count += 1;
    if (formik.values.created_at_to) count += 1;
    return count;
  }, [
    formik.values.customer_id,
    formik.values.status,
    formik.values.legal_entity_id,
    formik.values.responsible_id,
    formik.values.created_at_from,
    formik.values.created_at_to,
  ]);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  const { customers: customersState } = useAppSelector((state: RootState) => state.customers);

  const customerOptions = useMemo(() => {
    const items = customersState?.items || [];
    return items.map((c: any) => ({ value: String(c.id), label: c.name }));
  }, [customersState?.items]);

  const projectStatusOptions = useMemo(() => {
    const list: any = guides?.ProjectStatus;
    if (!list) return [] as { value: string; label: string }[];
    const options: { value: string; label: string }[] = [];
    for (const key in list) {
      if (Object.hasOwnProperty.call(list, key)) {
        const val = String(list[key]);
        options.push({ value: key, label: val });
      }
    }
    return options;
  }, [guides]);

  const legalEntityOptions = useMemo(() => {
    const list: any = guides?.LegalEntity;
    if (!list) return [] as { value: string; label: string }[];
    const options: { value: string; label: string }[] = [];
    for (const key in list) {
      if (Object.hasOwnProperty.call(list, key)) {
        const val = String(list[key]);
        options.push({ value: key, label: val });
      }
    }
    return options;
  }, [guides]);

  const handleOpenFilters = () => {
    onFiltersOpen();
  };

  const columns = useMemo<Array<ColumnDef<Project, any>>>(
    () => [
      columnHelper.accessor("name", {
        header: "Название",
        cell: (info) => (
          <Text fontWeight={600} color="#3b515e" fontSize={14}>
            {info.getValue()}
          </Text>
        ),
        enableColumnFilter: true,
        size: 220,
        minSize: 160,
      }),
      columnHelper.accessor("customer.name", {
        id: "customer",
        header: "Заказчик",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="160px" fontWeight={600} color="#3b515e" fontSize={14}>
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 120,
        minSize: 80,
      }),
      columnHelper.accessor("status.name", {
        id: "status",
        header: "Статус проекта",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            {StatusCell(info.getValue())}
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 120,
        minSize: 80,
      }),
      columnHelper.accessor("legal_entity.name", {
        header: "Юр. лицо",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text>{info.getValue()}</Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 160,
        minSize: 120,
      }),
      columnHelper.accessor("responsible_user.name", {
        header: "Ответственный аккаунт-менеджер",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="160px" fontWeight={600} color="#3b515e" fontSize={14}>
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        size: 160,
        minSize: 120,
      }),
      columnHelper.accessor("created_at", {
        header: "Дата создания",
        cell: (info) => (
          <Tooltip label={formatDate(info.getValue() || "")} hasArrow>
            <Text noOfLines={1} maxW="160px" fontWeight={600} color="#3b515e" fontSize={14}>
              {formatDate(info.getValue() || "")}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        enableSorting: true,
        size: 120,
        minSize: 80,
      }),
    ],
    [],
  );

  return (
    <Layout>
      <Flex flexDirection="column" maxH="90vh" overflowY="auto" gap="24px">
        <Text fontSize={27} fontWeight={500}>
          Проекты
        </Text>
        <BaseTableFilters
          handleSearch={handleSearch}
          placeHolderInput="Поиск по имени заказчика"
          searchValue={formik.values.search || ""}
          isShowBtnPopupFilters
          selectedFiltersCount={appliedFiltersCount}
          onClickBtnPopupFilters={handleOpenFilters}
          isOpenPopupFilters={isFiltersOpen}
          onClosePopupFilters={onFiltersClose}
          popupFilters={
            <ProjectsPopupFilter
              values={formik.values}
              onApply={(v) => {
                handleApplyFilters(v);
                onFiltersClose();
              }}
              onReset={() => {
                handleResetFilters();
                onFiltersClose();
              }}
              customerOptions={customerOptions}
              projectStatusOptions={projectStatusOptions}
              legalEntityOptions={legalEntityOptions}
              recruiters={recruiters}
            />
          }
          isLoading={isLoading}
          isShowCreateButton={projects.can_add}
          onClickCreate={handleCreate}
        />

        <BaseTable
          list={projects.items}
          columns={columns as Array<ColumnDef<Project, any>>}
          sortHandler={({ id, direction }) => handleSort({ id, direction: direction ?? null })}
          sortState={{
            column: sortColumn || "",
            direction: sortDirection,
          }}
          isLoading={isLoading}
        />

        <Pagination
          totalItems={totalItems}
          isLoading={isLoading}
          onPageChange={(offset, limit) => {
            const params = new URLSearchParams(searchParams);
            params.set("page", String(offset / limit + 1));
            params.set("limit", String(limit));
            navigate(`?${params.toString()}`);
          }}
        />
      </Flex>
    </Layout>
  );
};
